// @ts-nocheck
// Call-centre chooser for platform (super / sub-super admin) and company
// (reseller / company admin) viewers. Loads GET /live-calls/scopes once and
// shows ONE dropdown whose options are the call-centre names, each with its
// basic live totals (in / out / int / total) from GET /live-calls/counts.
// Counts are only polled while the dropdown is open (all centres) or a centre
// is selected (that centre), refreshed every COUNTS_MS while the tab is visible.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { API_LIVE_CALLS_COUNTS, API_LIVE_CALLS_SCOPES } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { usePageVisible } from '@/hooks/usePageVisible';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { nf } from '@/features/cti2/team/teamUi';
import type { LiveCallCallCentre, LiveCallCounts } from '../types';

const COUNTS_MS = 5000;

interface Props {
  selected: LiveCallCallCentre | null;
  onSelect: (cc: LiveCallCallCentre) => void;
}


const Stat = ({ label, value, tone }: { label: string; value: any; tone: string }) => (
  <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-medium', tone)}>
    {label}
    <b className="tabular-nums">{value === null || value === undefined ? '—' : nf(value)}</b>
  </span>
);

export default function CallCentrePicker({ selected, onSelect }: Props) {
  const [centres, setCentres] = useState<LiveCallCallCentre[] | null>(null);
  const [counts, setCounts] = useState<Record<number, LiveCallCounts>>({});
  const [open, setOpen] = useState(false);
  const visible = usePageVisible();

  useEffect(() => {
    getApiCall(API_LIVE_CALLS_SCOPES)
      .then((data) => {
        const list = Array.isArray(data?.callCentres) ? data.callCentres : [];
        setCentres(list);
        if (!selected && list.length === 1) onSelect(list[0]);
      })
      .catch((err) => {
        setCentres([]);
        toast.error(friendlyApiError(err) || 'Unable to load call centres');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const wanted = useMemo(() => {
    if (!centres?.length) return [];
    if (open) return centres;
    return selected ? centres.filter((c) => c.ccId === selected.ccId) : [];
  }, [centres, open, selected]);

  const refreshCounts = useCallback(async () => {
    if (!wanted.length) return;
    const results = await Promise.allSettled(
      wanted.map((cc) =>
        getApiCall(API_LIVE_CALLS_COUNTS, { companyId: cc.companyId, ccId: cc.ccId }).then((d) => [cc.ccId, d?.counts]),
      ),
    );
    const next = {};
    results.forEach((r) => {
      if (r.status === 'fulfilled' && r.value?.[1]) next[r.value[0]] = r.value[1];
    });
    setCounts((prev) => ({ ...prev, ...next }));
  }, [wanted]);

  useEffect(() => {
    if (!visible || !wanted.length) return undefined;
    refreshCounts();
    const t = setInterval(refreshCounts, COUNTS_MS);
    return () => clearInterval(t);
  }, [visible, wanted, refreshCounts]);

  if (centres === null) {
    return <Skeleton className="h-10 w-full max-w-md rounded-lg" />;
  }
  if (!centres.length) {
    return <EmptyState icon={Building2} title="No call centres" description="Nothing to monitor under this account yet." />;
  }

  const selectedCounts = selected ? counts[selected.ccId] : null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Call centre</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Select call centre"
            className="h-10 w-full max-w-md justify-between gap-2 font-normal sm:w-[360px]"
          >
            <span className="flex min-w-0 items-center gap-2">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className={cn('truncate', !selected && 'text-muted-foreground')}>
                {selected ? selected.name || `Call centre ${selected.ccId}` : 'Select a call centre'}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {selected ? (
                <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-primary">
                  {selectedCounts ? nf(selectedCounts.total) : '—'} live
                </span>
              ) : null}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(92vw,440px)] p-0">
          <Command>
            <CommandInput placeholder="Search call centre…" />
            <CommandList className="max-h-[340px]">
              <CommandEmpty>No call centre found</CommandEmpty>
              <CommandGroup>
                {centres.map((cc) => {
                  const k = counts[cc.ccId];
                  const active = selected?.ccId === cc.ccId;
                  return (
                    <CommandItem
                      key={`${cc.companyId}-${cc.ccId}`}
                      value={`${cc.name || ''} ${cc.ccId}`}
                      onSelect={() => {
                        onSelect(cc);
                        setOpen(false);
                      }}
                      className="items-start gap-2 py-2"
                    >
                      <Check className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'opacity-100' : 'opacity-0')} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-[13px] font-semibold">{cc.name || `Call centre ${cc.ccId}`}</span>
                          <span className="shrink-0 text-[10px] text-muted-foreground">cc {cc.ccId}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          <Stat label="Total" value={k ? k.total : null} tone="bg-primary/10 text-primary" />
                          <Stat label="In" value={k ? k.in : null} tone="bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300" />
                          <Stat label="Out" value={k ? k.out : null} tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" />
                          <Stat label="Int" value={k ? k.int : null} tone="bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" />
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected && selectedCounts ? (
        <span className="hidden items-center gap-1.5 text-xs text-muted-foreground md:inline-flex">
          In <b className="tabular-nums text-foreground">{nf(selectedCounts.in)}</b>
          · Out <b className="tabular-nums text-foreground">{nf(selectedCounts.out)}</b>
          · Int <b className="tabular-nums text-foreground">{nf(selectedCounts.int)}</b>
        </span>
      ) : null}
    </div>
  );
}
